/* createListStore — factory for module-level, TTL-cached list stores.
 *
 * Same pattern proven in `casesStore.js`: a single cached snapshot shared
 * across all components that read it via the `useX()` hook, with inflight
 * promise dedupe so concurrent readers never trigger duplicate requests.
 *
 * Use it for READ-heavy list endpoints where the data is safe to cache
 * for ~30 seconds and doesn't have server-push (SSE/websocket)
 * invalidation. Do NOT use for SSE-driven streams (Messages) where
 * freshness is critical every render — the SSE handler would have to
 * invalidate() on every event, defeating the cache.
 *
 * Returns:
 *   { fetch, prefetch, invalidate, useData }
 *   • fetch({force})   → Promise<data>
 *   • prefetch()       → void  (fire-and-forget)
 *   • invalidate()     → Promise<data>  (clears cache + refetch)
 *   • useData()        → { data, loading, error, refresh }
 */
import { useEffect, useSyncExternalStore } from "react";

export function createListStore({ fetcher, staleMs = 30_000, name = "list" }) {
  let _cache = { data: null, loadedAt: 0, error: null };
  let _inflight = null;
  const _listeners = new Set();
  const notify = () => { for (const cb of _listeners) cb(); };

  async function fetch({ force = false } = {}) {
    if (_inflight) return _inflight;
    if (
      !force &&
      _cache.data !== null &&
      Date.now() - _cache.loadedAt < staleMs
    ) {
      return _cache.data;
    }
    _inflight = (async () => {
      try {
        const data = await fetcher();
        _cache = { data, loadedAt: Date.now(), error: null };
        notify();
        return data;
      } catch (err) {
        _cache = { ..._cache, error: err };
        notify();
        throw err;
      } finally {
        _inflight = null;
      }
    })();
    return _inflight;
  }

  function prefetch() {
    fetch().catch(() => { /* silent — hook consumer will surface */ });
  }

  function invalidate() {
    // Stale-while-revalidate: keep serving the CURRENT data to subscribers
    // and refetch quietly in the background. Previously we nuked
    // `_cache.data = null` here, which flipped every mounted consumer's
    // `loading` flag to `true` for the ~100 ms it took to refetch — on
    // pages hit by frequent SSE invalidations (Messages) the user saw
    // a "جاري التحميل" flash every 3-4 seconds. That was the bug.
    // Now the data stays on screen; a fresh snapshot lands silently
    // when fetch resolves. Keeps the exact same public API.
    _cache = { ..._cache, loadedAt: 0 };
    return fetch({ force: true });
  }

  /* patch(mutator) — apply an in-memory mutation to the cached list
   * WITHOUT a network round-trip. Use for SSE-driven local updates
   * (e.g. new message preview lands, typing indicator toggles) where
   * the server-of-truth is being pushed to us in real time and a
   * refetch would just re-derive the same data 400 ms later.
   *
   * `mutator` receives the current array and returns a new array.
   * Returning the same reference is a no-op. */
  function patch(mutator) {
    if (!Array.isArray(_cache.data)) return;
    const next = mutator(_cache.data);
    if (next === _cache.data || next == null) return;
    _cache = { ..._cache, data: next };
    notify();
  }

  function subscribe(cb) { _listeners.add(cb); return () => _listeners.delete(cb); }
  function snapshot() { return _cache; }

  function useData() {
    const cache = useSyncExternalStore(subscribe, snapshot, snapshot);
    useEffect(() => {
      if (_cache.data === null || Date.now() - _cache.loadedAt > staleMs) {
        fetch().catch(() => {});
      }
    }, []);
    return {
      data: cache.data,
      loading: cache.data === null && !cache.error,
      error: cache.error,
      refresh: () => invalidate(),
      _name: name,
    };
  }

  return { fetch, prefetch, invalidate, patch, useData };
}
